import React, { useMemo, useRef } from 'react';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import { showTimeByDateType, newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import useSinglePieCharts from '@/pages/intelligence/components/Chart/hooks/useSinglePieCharts';
import useBarCharts from '@/pages/intelligence/components/Chart/hooks/useBarCharts';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import { getIfPercentage } from '@/pages/intelligence/utils/common';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';

// 每个榜单，组装好各类可视化需要的数据，传递给可视化统一组件
const Visualis: React.FC<any> = (props: any) => {
  const {
    chartType = ChartType.Line,
    dateType,
    startDate,
    endDate,
    lineData = [], // 折线 数据
    isNodata,
    imageType,
    metric,
    titleNow,
    chartLoading,
    setChartLoading,
    topChartsType,
    renderType,
    tableData,
  } = props;
  const cardRef = useRef(null);
  const chartsInstance = useRef<IChartsIndexInstance>(null);

  const isPercentLine = useMemo(() => {
    switch (renderType) {
      case RenderType.GROWTH_ONLY:
        return true;
      default:
        return false;
    }
  }, [topChartsType, renderType]);

  const barNameList = useMemo(() => {
    if (!Array.isArray(tableData) || tableData?.length < 1) return [];
    return (
      tableData?.map((item: any) => {
        const nameStr = item?.name || item?.game_name || '';
        return nameStr;
      }) || []
    );
  }, [tableData]);

  const barSeriesData = useMemo(() => {
    return tableData?.map((item: any, i: number): any => {
      const valueObj = item.metric_info?.find((item: any) => item.metric == 'MAU');
      let barNum = valueObj.value ? valueObj?.value : 0;
      if (getIfPercentage(metric)) {
        barNum = barNum * 100;
      }
      const nameStr = item?.name || item?.game_name || '';
      return {
        type: 'bar',
        name: nameStr,
        value: barNum,
        cover: item.cover == '' ? null : item.cover,
        unifiedId: item?.game_name == 'Others' ? 'Others' : item.game_id,
        itemStyle: {
          color: ECHARTSCOLOR[i],
        },
        label: {
          show: true,
          position: 'top',
          color: '#444',
          distance: 4,
          fontSize: 12,
          formatter: (params: any) =>
            `${newMathFormat(Number(params.value), true) + (getIfPercentage(metric) ? '%' : '')}`,
        },
      };
    });
  }, [tableData, metric, startDate, endDate]);

  // 获取柱状图hooks
  const { barOption } = useBarCharts({
    seriesData: barSeriesData,
    barOrPieData: barSeriesData,
    setChartLoading,
    imageType,
    barNameList,
    // topChartsType: 'pcconsole-country-bar',
    cardWidth: cardRef?.current?.clientWidth,
    chartType,
    ifNeedSourceImg: false,
    startDate,
    endDate,
    secondTitleRight: 'MAU',
  });

  const pieSeriesData = useMemo(() => {
    const piList: any = [];
    if (!Array.isArray(tableData) || tableData?.length < 1) return [];
    tableData?.forEach((item: any, i: any) => {
      const valueObj = item.metric_info?.find((item: any) => item.metric == 'MAU');
      const metricNum = valueObj.value ? valueObj?.value : 0;
      const nameStr = item?.name || item?.game_name || '';
      const objTemp = {
        app_id: item.game_id,
        name: nameStr,
        value: metricNum,
      };
      piList.push(objTemp);
    });
    return piList;
  }, [tableData, metric]);

  // 获取饼图hooks
  const { pieOption } = useSinglePieCharts({
    pieData: pieSeriesData,
    secondTitleRight: 'MAU',
    setChartLoading,
    chartLoading,
    chartType,
    chartsInstance,
    titleNow,
    startDate,
    endDate,
  });

  // 组装rpd折线图数据
  const getRpdTypeData = (list: any) => {
    if (!Array.isArray(list) || list?.length < 1) return [];
    const arr: any = [];
    for (const i in list) {
      arr.push({
        time: showTimeByDateType(list[i].date, dateType?.toLocaleLowerCase()),
        value: list[i].value || null,
      });
    }
    return arr;
  };

  const getMetricsList = (list: any) => {
    if (!Array.isArray(list) || list?.length < 1) return [];
    const arr: any = [];
    for (const n in list) {
      const objTemp = {
        cover: list[n].cover,
        id: list[n].game_id,
        name: list[n].game_name,
        history: getRpdTypeData(list[n].date_info || []),
      };
      arr.push(objTemp);
    }
    return arr;
  };

  const lineSeriesData = useMemo(() => {
    if (!Array.isArray(lineData) || lineData?.length < 1) return {};
    const temp = {
      source: getMetricsList(lineData || []),
    };
    return temp;
  }, [lineData, metric]);

  // 获取折线图hooks
  const { lineOption } = useLineCharts({
    lineData: lineSeriesData,
    metric: 'source',
    // topChartsType: 'pcconsole-stats-line',
    imageType,
    secondTitleRight: 'MAU',
    chartType,
    setChartLoading,
    dateType,
    isPercentLine,
    ifNeedMoveZero: false,
  });

  return (
    <>
      <div className="card-line" ref={cardRef}>
        <ChartsIndex
          ref={chartsInstance}
          chartLoading={chartLoading}
          lineOption={lineOption}
          barOption={barOption}
          pieOption={pieOption}
          chartType={chartType}
          isNodata={isNodata}
        />
      </div>
    </>
  );
};

export default Visualis;
